import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, PageHeader, Field, Input, Select, Badge } from "@/components/ui-kit";
import { Calendar, TrendingUp, Users, AlertCircle, Clock4 } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Cell, LineChart, Line, Legend } from "recharts";
import { DatePicker } from "@/components/DatePicker";

/**
 * Iter 65 (#3) — HR Absensi Insights Dashboard.
 *
 * Backed by:
 *   • /api/attendance/alpha-summary   — alpha (no clock-in) per employee in date range
 *   • /api/attendance/admin           — raw attendance rows w/ is_late + is_half_day flags
 *
 * Sections:
 *   • KPI cards: total alpha, late, half-day, on-time%
 *   • Weekly trend (line chart) — late / alpha / half-day per week
 *   • Top 10 employees most alpha (bar chart)
 *   • Departmen heatmap-ish: alpha breakdown per department category
 */
export default function AbsensiAnalytics() {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const toIso = (d) => d.toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(toIso(firstOfMonth));
  const [endDate, setEndDate] = useState(toIso(today));
  const [deptCat, setDeptCat] = useState("");
  const [deptOptions, setDeptOptions] = useState([]);
  const [alphaRows, setAlphaRows] = useState([]);
  const [attRows, setAttRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/department-categories").then((r) => setDeptOptions(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;
    setLoading(true);
    const params = { start_date: startDate, end_date: endDate };
    if (deptCat) params.department_category = deptCat;
    Promise.all([
      api.get("/attendance/alpha-summary", { params, signal: ac.signal }),
      api.get("/attendance/admin", { params: { from: startDate, to: endDate, ...(deptCat ? { department_category: deptCat } : {}) }, signal: ac.signal }),
    ])
      .then(([a, b]) => {
        if (cancelled) return;
        setAlphaRows(a.data?.rows || []);
        // Defensive unwrap — /api/attendance/admin returns either an array or {rows:[]}.
        const rows = Array.isArray(b.data) ? b.data : (b.data?.rows || []);
        setAttRows(rows);
      })
      .catch(() => { if (!cancelled) { setAlphaRows([]); setAttRows([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; ac.abort(); };
  }, [startDate, endDate, deptCat]);

  // ---- KPI aggregates ----
  const kpi = useMemo(() => {
    const totalAlpha = alphaRows.reduce((s, r) => s + (r.alpha_count || 0), 0);
    const totalLate = attRows.filter((r) => r.is_late === true).length;
    const totalHalf = attRows.filter((r) => r.is_half_day === true).length;
    const total = attRows.length;
    const onTime = total - totalLate;
    const onTimePct = total > 0 ? Math.round((onTime / total) * 1000) / 10 : 0;
    return { totalAlpha, totalLate, totalHalf, total, onTimePct, employees: alphaRows.length };
  }, [alphaRows, attRows]);

  // ---- Weekly trend ----
  const weeklyTrend = useMemo(() => {
    const buckets = {};
    const isoWeek = (d) => {
      const dt = new Date(d);
      // ISO week: start on Monday
      const day = dt.getDay() || 7;
      const monday = new Date(dt);
      monday.setDate(dt.getDate() - (day - 1));
      return toIso(monday);
    };
    attRows.forEach((r) => {
      if (!r.date) return;
      const wk = isoWeek(r.date);
      const b = buckets[wk] || { week: wk, late: 0, half: 0 };
      if (r.is_late) b.late += 1;
      if (r.is_half_day) b.half += 1;
      buckets[wk] = b;
    });
    alphaRows.forEach((emp) => {
      (emp.alpha_dates || []).forEach((d) => {
        const wk = isoWeek(d);
        const b = buckets[wk] || { week: wk, late: 0, half: 0 };
        b.alpha = (b.alpha || 0) + 1;
        buckets[wk] = b;
      });
    });
    return Object.values(buckets)
      .map((b) => ({ ...b, alpha: b.alpha || 0 }))
      .sort((a, b) => a.week.localeCompare(b.week));
  }, [attRows, alphaRows]);

  // ---- Top 10 alpha ----
  const topAlpha = useMemo(
    () => [...alphaRows].sort((a, b) => (b.alpha_count || 0) - (a.alpha_count || 0)).slice(0, 10),
    [alphaRows],
  );

  // ---- Per department ----
  const perDept = useMemo(() => {
    const map = {};
    alphaRows.forEach((r) => {
      const k = r.department_category || "—";
      map[k] = (map[k] || 0) + (r.alpha_count || 0);
    });
    return Object.entries(map).map(([department, alpha]) => ({ department, alpha })).sort((a, b) => b.alpha - a.alpha);
  }, [alphaRows]);

  return (
    <div className="min-h-full bg-[#f6f5f0]" data-testid="absensi-analytics-page">
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader
        overline="HR · Analitik"
        title="Analitik Absensi"
        description="Insights kehadiran karyawan: alpha, terlambat, setengah-hari."
      />

      {/* Bento KPI hero */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-5 rounded-2xl bg-gradient-to-br from-stone-900 via-stone-800 to-blue-900 text-white p-6 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-blue-500/20 blur-3xl"></div>
          <div className="relative">
            <div className="text-[11px] uppercase tracking-widest text-stone-400">Tepat Waktu</div>
            <div className="font-display text-5xl mt-2">{kpi.onTimePct}%</div>
            <div className="text-xs text-stone-400 mt-1">Dari total {kpi.total} record absensi</div>
            <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/10 text-xs">
              <div>
                <div className="text-stone-400">Total Alpha</div>
                <div className="font-mono-num text-red-300 text-lg">{kpi.totalAlpha}</div>
              </div>
              <div>
                <div className="text-stone-400">Telat</div>
                <div className="font-mono-num text-amber-300 text-lg">{kpi.totalLate}</div>
              </div>
              <div>
                <div className="text-stone-400">Half-Day</div>
                <div className="font-mono-num text-blue-300 text-lg">{kpi.totalHalf}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-6 md:col-span-3 rounded-2xl bg-white border border-stone-200/70 p-5">
          <Users className="w-5 h-5 text-blue-700 mb-2"/>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Karyawan Aktif</div>
          <div className="font-display text-3xl mt-1 text-stone-900">{kpi.employees}</div>
        </div>
        <div className="col-span-6 md:col-span-2 rounded-2xl bg-red-50/60 border border-red-200 p-4">
          <AlertCircle className="w-4 h-4 text-red-600 mb-2"/>
          <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">Alpha</div>
          <div className="font-display text-2xl mt-1 text-red-800">{kpi.totalAlpha}</div>
        </div>
        <div className="col-span-6 md:col-span-2 rounded-2xl bg-amber-50/60 border border-amber-200 p-4">
          <Clock4 className="w-4 h-4 text-amber-600 mb-2"/>
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Terlambat</div>
          <div className="font-display text-2xl mt-1 text-amber-800">{kpi.totalLate}</div>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Tanggal Mulai">
            <DatePicker value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="abs-anal-start" />
          </Field>
          <Field label="Tanggal Akhir">
            <DatePicker value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="abs-anal-end" />
          </Field>
          <Field label="Kategori Dept (opsional)">
            <Select value={deptCat} onChange={(e) => setDeptCat(e.target.value)} data-testid="abs-anal-dept">
              <option value="">Semua</option>
              {deptOptions.map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
            </Select>
          </Field>
          <div className="flex items-end">
            {loading && <span className="text-xs text-stone-500">Memuat...</span>}
          </div>
        </div>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi icon={AlertCircle} label="Total Alpha" value={kpi.totalAlpha} color="text-red-700" testid="kpi-alpha" />
        <Kpi icon={Clock4} label="Total Terlambat" value={kpi.totalLate} color="text-amber-700" testid="kpi-late" />
        <Kpi icon={Calendar} label="Setengah Hari" value={kpi.totalHalf} color="text-orange-700" testid="kpi-half" />
        <Kpi icon={TrendingUp} label="On-time %" value={`${kpi.onTimePct}%`} color="text-emerald-700" testid="kpi-ontime" />
        <Kpi icon={Users} label="Karyawan Aktif" value={kpi.employees} color="text-stone-700" testid="kpi-employees" />
      </div>

      {/* Weekly Trend */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-stone-800 mb-3">Tren Mingguan</h3>
        {weeklyTrend.length === 0 ? (
          <div className="text-xs text-stone-400 py-8 text-center">Tidak ada data dalam rentang ini.</div>
        ) : (
          <div className="h-64" data-testid="weekly-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weeklyTrend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="week" fontSize={11} stroke="#78716c" />
                <YAxis fontSize={11} stroke="#78716c" allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="alpha" name="Alpha" stroke="#b91c1c" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="late" name="Terlambat" stroke="#d97706" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="half" name="Setengah Hari" stroke="#ea580c" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Top 10 Alpha */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-stone-800 mb-3">Top 10 Karyawan Paling Sering Alpha</h3>
        {topAlpha.length === 0 ? (
          <div className="text-xs text-stone-400 py-8 text-center">Tidak ada karyawan alpha di rentang ini.</div>
        ) : (
          <div className="h-72" data-testid="top-alpha-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topAlpha} layout="vertical" margin={{ top: 5, right: 20, left: 110, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis type="number" fontSize={11} stroke="#78716c" allowDecimals={false} />
                <YAxis type="category" dataKey="employee_name" fontSize={11} stroke="#78716c" width={100} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="alpha_count" name="Alpha" fill="#dc2626">
                  {topAlpha.map((entry, idx) => (
                    <Cell key={idx} fill={entry.alpha_count >= 5 ? "#991b1b" : entry.alpha_count >= 3 ? "#dc2626" : "#f59e0b"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Per Department */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-stone-800 mb-3">Alpha per Kategori Dept</h3>
        {perDept.length === 0 ? (
          <div className="text-xs text-stone-400 py-8 text-center">Tidak ada data.</div>
        ) : (
          <table className="w-full text-sm" data-testid="per-dept-table">
            <thead>
              <tr className="border-b border-stone-200">
                <th className="text-left px-3 py-2 text-xs font-semibold text-stone-600">Kategori</th>
                <th className="text-right px-3 py-2 text-xs font-semibold text-stone-600">Total Alpha</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-stone-600 w-2/5">Visual</th>
              </tr>
            </thead>
            <tbody>
              {perDept.map((r) => {
                const max = perDept[0]?.alpha || 1;
                const pct = max ? (r.alpha / max) * 100 : 0;
                return (
                  <tr key={r.department} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-3 py-2 text-stone-800">{r.department}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.alpha}</td>
                    <td className="px-3 py-2">
                      <div className="h-2 bg-stone-100 rounded-sm overflow-hidden">
                        <div className="h-full bg-red-600 rounded-sm" style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-sm bg-stone-50 ${color}`}><Icon className="h-5 w-5" /></div>
        <div>
          <div className="text-xs text-stone-500">{label}</div>
          <div className={`text-xl font-bold ${color}`}>{value}</div>
        </div>
      </div>
    </Card>
  );
}
